import { useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { LpHero } from '@/components/lp/LpHero';
import { LpTrustStrip } from '@/components/lp/LpTrustStrip';
import { LpFeatures } from '@/components/lp/LpFeatures';
import { LpForm } from '@/components/lp/LpForm';
import { LpWhyKGOB } from '@/components/lp/LpWhyKGOB';
import { getLpConfig } from '@/pages/lp/lpConfig';

/**
 * LandingPage — shared component that renders one of the 6 PPC landing pages
 * based on the :slug URL parameter. Reads from lpConfig.js.
 *
 * Routes: /lp/:slug   (e.g. /lp/tax-planning)
 *
 * SEO: noindex,follow so PPC pages don't dilute organic ranking.
 *      Also Disallow: /lp/ in robots.txt and excluded from sitemap.
 *      We set the robots meta via direct DOM (alongside Helmet) because the
 *      site-wide Helmet config has a known quirk where some meta tags don't
 *      override defaults from index.html reliably.
 */
const LandingPage = () => {
  const { slug } = useParams();
  const config = getLpConfig(slug);

  // Defense-in-depth: ensure robots noindex is present in DOM regardless of Helmet.
  useEffect(() => {
    if (!config) return;
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    const prev = robots.getAttribute('content');
    robots.setAttribute('content', 'noindex,follow');
    return () => {
      // Restore on unmount so other pages aren't affected
      if (prev) robots.setAttribute('content', prev);
      else robots.remove();
    };
  }, [config]);

  if (!config) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{config.h1} | KGOB CPAs</title>
        <meta name="description" content={config.subhead} />
        <meta name="robots" content="noindex,follow" />
        <link rel="canonical" href={`https://kgob.com/lp/${config.slug}`} />
      </Helmet>

      <Header />

      <main data-testid={`lp-page-${config.slug}`}>
        <LpHero config={config} />
        <LpTrustStrip />
        <LpFeatures config={config} />
        <LpForm config={config} />
        <LpWhyKGOB />
      </main>

      <Footer />
    </div>
  );
};

export default LandingPage;
